<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useModuleGate } from 'dashboard/composables/useModuleGate';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import CampaignsToolbar from 'dashboard/components-next/Campaigns/CampaignsToolbar.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

// Tela do módulo 'campaigns': sem o módulo, paywall bloqueante no mount.
useModuleGate('campaigns');
import { useCampaignPagination } from 'dashboard/composables/useCampaignPagination';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const router = useRouter();

const campaignPendingDelete = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

// Lista UNIFICADA (menu "Campanhas"): todos os canais numa lista só — o
// canal aparece pelo ícone da inbox no card.
const WhatsAppCampaigns = computed(
  () => getters['campaigns/getAllCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const slugify = value =>
  value
    ?.toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || '';

const buildReportParam = campaign => {
  if (!campaign?.id) return null;
  const slug = slugify(campaign.title);
  return slug ? `${campaign.id}-${slug}` : String(campaign.id);
};

const handleDelete = campaign => {
  campaignPendingDelete.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handleEdit = campaign => {
  // Canvas primeiro: edição reconstitui os blocos (wizard ficou de legado).
  router.push({
    name: 'campaigns_whatsapp_flow',
    params: { accountId: route.params.accountId, campaignId: campaign.id },
  });
};

const handleDuplicate = async campaign => {
  try {
    const data = {
      campaign: {
        title: `${campaign.title} (${t('CAMPAIGN.CARD.COPY')})`,
        description: campaign.description || '',
        message: campaign.message || '',
        message_sequence: campaign.message_sequence || '',
        inbox_id: campaign.inbox?.id || campaign.inbox_id,
        audience: campaign.audience || [],
        audience_type: campaign.audience_type || 'tags',
        scheduled_at: new Date().toISOString(),
      },
    };
    await store.dispatch('campaigns/create', data);
    await store.dispatch('campaigns/get');
  } catch {
    // silently fail
  }
};

// Progresso ao vivo na lista: enquanto houver campanha rodando, atualiza a
// cada 15s (o card mostra a barra andando sem precisar abrir o relatório).
const POLL_INTERVAL_MS = 15000;
let pollTimer = null;

const hasRunningCampaign = computed(() =>
  (WhatsAppCampaigns.value || []).some(c => c.campaign_status === 'running')
);

watch(
  hasRunningCampaign,
  running => {
    clearInterval(pollTimer);
    if (running) {
      pollTimer = setInterval(
        () => store.dispatch('campaigns/get'),
        POLL_INTERVAL_MS
      );
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => clearInterval(pollTimer));

const refreshCampaigns = async () => {
  await store.dispatch('campaigns/get');
};

// Canvas de fluxo (ramos por resultado) — editor por campanha. Campanha do
// modo antigo (sem flow salvo) NÃO ganha o botão: abrir levava a um canvas
// remontado que parecia "outro fluxo".
const resolveFlowRoute = campaign => {
  if (!campaign?.id) return null;
  if (!campaign?.flow?.nodes?.length) return null;
  return {
    name: 'campaigns_whatsapp_flow',
    params: {
      accountId: route.params.accountId,
      campaignId: campaign.id,
    },
  };
};

const resolveReportRoute = campaign => {
  if (!campaign?.id) return null;
  const param = buildReportParam(campaign);
  if (!param) return null;
  return {
    name: 'campaigns_whatsapp_report',
    params: {
      accountId: route.params.accountId,
      campaignParam: param,
    },
  };
};

// Search, Audience, Inbox and Status Filters
const searchValue = ref('');
const selectedLabels = ref([]);
const selectedInboxes = ref([]);
const filterOperator = ref('and');
const selectedStatuses = ref([]);

const stripHtml = s => (s || '').toString().replace(/<[^>]*>/g, ' ');
const contains = (hay, needle) =>
  (hay || '').toLowerCase().includes((needle || '').toLowerCase());

const normalizeStatus = s => {
  if (typeof s === 'number') {
    switch (s) {
      case 0:
        return 'active';
      case 1:
        return 'completed';
      case 2:
        return 'error';
      case 3:
        return 'running';
      case 4:
        return 'paused';
      case 5:
        return 'draft';
      default:
        return 'active';
    }
  }
  if (typeof s === 'string') {
    const v = s.toLowerCase();
    if (
      ['active', 'completed', 'error', 'running', 'paused', 'draft'].includes(v)
    )
      return v;
  }
  return 'active';
};

const filteredWhatsAppCampaigns = computed(() => {
  const list = WhatsAppCampaigns.value || [];
  const q = searchValue.value?.trim().toLowerCase();
  const selected = new Set(
    Array.isArray(selectedLabels.value)
      ? selectedLabels.value.map(labelId => String(labelId))
      : []
  );

  return list.filter(c => {
    const title = c.title || '';
    const message = stripHtml(c.message || '');
    const templateName = c.template_params?.name || '';
    const matchesSearch =
      !q ||
      contains(title, q) ||
      contains(message, q) ||
      contains(templateName, q);

    if (!matchesSearch) return false;

    const audienceIds = new Set(
      (c.audience || [])
        .filter(
          a => a && a.type === 'Label' && a.id !== null && a.id !== undefined
        )
        .map(a => String(a.id))
    );
    const hasLabelSelection = selected.size > 0;
    const labelMatch =
      !hasLabelSelection ||
      Array.from(selected).every(id => audienceIds.has(id));

    const inboxIds = Array.isArray(selectedInboxes.value)
      ? selectedInboxes.value.map(inboxId => String(inboxId))
      : [];
    const hasInboxSelection = inboxIds.length > 0;
    const inboxId = c.inbox?.id;
    const inboxMatch =
      !hasInboxSelection ||
      (inboxId != null && inboxIds.includes(String(inboxId)));

    // Status match (active/completed/error/running)
    const statuses = Array.isArray(selectedStatuses.value)
      ? selectedStatuses.value
      : [];
    const hasStatusSelection = statuses.length > 0;
    const statusValue = normalizeStatus(c.campaign_status ?? c.status);
    const statusMatch = !hasStatusSelection || statuses.includes(statusValue);

    if (filterOperator.value === 'and') {
      return labelMatch && inboxMatch && statusMatch;
    }
    const activeMatches = [];
    if (hasLabelSelection) activeMatches.push(labelMatch);
    if (hasInboxSelection) activeMatches.push(inboxMatch);
    if (hasStatusSelection) activeMatches.push(statusMatch);
    return activeMatches.length === 0 ? true : activeMatches.some(Boolean);
  });
});

const {
  currentPage,
  paginatedCampaigns: paginatedWhatsAppCampaigns,
  totalItems,
  shouldShowPagination,
  itemsPerPage,
  updateCurrentPage,
} = useCampaignPagination(filteredWhatsAppCampaigns);
</script>

<template>
  <CampaignLayout
    data-tour="campanhas-nova"
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="
      router.push({
        name: 'campaigns_whatsapp_canvas',
        params: { accountId: route.params.accountId },
      })
    "
  >
    <CampaignsToolbar
      v-if="!hasNoWhatsAppCampaigns"
      v-model="selectedLabels"
      v-model:inboxes="selectedInboxes"
      v-model:statuses="selectedStatuses"
      v-model:operator="filterOperator"
      :search-value="searchValue"
      @search="val => (searchValue = val)"
    />
    <template #left-action>
      <Button
        icon="i-lucide-rotate-cw"
        size="sm"
        :is-loading="isFetchingCampaigns"
        :disabled="isFetchingCampaigns"
        @click="refreshCampaigns"
      >
        <span class="hidden sm:inline">{{ t('CAMPAIGN.REFRESH') }}</span>
      </Button>
    </template>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <template v-else-if="!hasNoWhatsAppCampaigns">
      <template v-if="filteredWhatsAppCampaigns.length">
        <CampaignList
          :campaigns="paginatedWhatsAppCampaigns"
          :report-route-resolver="resolveReportRoute"
          :flow-route-resolver="resolveFlowRoute"
          @edit="handleEdit"
          @delete="handleDelete"
          @duplicate="handleDuplicate"
        />
      </template>
      <div v-else class="flex items-center justify-center py-10">
        <span class="text-base text-n-slate-11">
          {{ $t('CAMPAIGNS.LIST.NO_RESULTS') }}
        </span>
      </div>
    </template>
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="campaignPendingDelete"
    />
    <template
      v-if="
        !hasNoWhatsAppCampaigns &&
        filteredWhatsAppCampaigns.length &&
        shouldShowPagination
      "
      #footer
    >
      <PaginationFooter
        :current-page="currentPage"
        :total-items="totalItems"
        :items-per-page="itemsPerPage"
        @update:current-page="updateCurrentPage"
      />
    </template>
  </CampaignLayout>
</template>
